/// <amd-module name="TFS/Diag/ActionsMonitor/ActionMonitorPane" />
import * as React from "react";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Icon } from "VSSUI/Icon";
import { Panel } from "VSSUI/Panel";
import { Tab, TabBar } from "VSSUI/Tabs";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, noop } from "VSSUI/Util";
import { ActionMonitorObjectTree } from "./ActionMonitorObjectTree";
import { IActionRecord } from "./ActionMonitorStore";

export interface IActionMonitorPaneProps {
    firedActions: IActionRecord[];
    onClearHistory: () => void;
    onDismiss: () => void;
    onClearActionRecord: (index: number) => void;
}

export interface IActionMonitorPaneState {
    expansionState: { [key: number]: boolean };
}

export class ActionMonitorPane extends React.Component<IActionMonitorPaneProps, IActionMonitorPaneState> {
    constructor(props: IActionMonitorPaneProps) {
        super(props);
        this.state = { expansionState: {} };
    }

    public render(): JSX.Element {
        return (
            <Panel
                modal={true}
                onDismiss={this.props.onDismiss}
                size={ContentSize.Large}
                className="actions-panel"
                titleProps={{
                    text: "Actions"
                }}
            >
                {this._renderPanelContent()}
            </Panel>
        );
    }

    private _renderPanelContent(): JSX.Element {
        const { firedActions, onClearHistory } = this.props;

        return (
            <div className="actions-panel-content flex flex-column flex-grow">
                <TabBar onSelectedTabChanged={noop} selectedTabId="history">
                    <Tab id="history" name="History" />
                </TabBar>
                <div className="actions-history-list">
                    {firedActions.map((a, i) => this._renderAction(i, a))}
                    {firedActions.length > 0 && this._renderEmptyTimeline()}
                </div>
                <div className="actions-history-button-container">
                    <Button primary={true} onClick={onClearHistory}>
                        Clear History
                    </Button>
                </div>
            </div>
        );
    }

    private _renderAction(index: number, action: IActionRecord): JSX.Element {
        const isExpanded = this._isRowExpanded(index);
        return (
            <ActionRecordComponent
                key={index}
                actionRecord={action}
                index={index}
                isExpanded={isExpanded}
                onClearActionRecord={this.props.onClearActionRecord}
                onToggleExpand={this._toggleExpand}
            />
        );
    }

    private _renderEmptyTimeline(): JSX.Element {
        return (
            <div className="action-record-container">
                <div className="action-record-timeline--empty" />
            </div>
        );
    }

    private _toggleExpand = (index: number): void => {
        this.setState({
            expansionState: {
                ...this.state.expansionState,
                [index]: !this.state.expansionState[index]
            }
        });
    };

    private _isRowExpanded(index: number): boolean {
        const { expansionState } = this.state;

        return !!expansionState[index];
    }
}

// ACTION RECORD ENTRY
interface IActionRecordComponentProps {
    actionRecord: IActionRecord;
    index: number;
    isExpanded: boolean;
    onClearActionRecord: (index: number) => void;
    onToggleExpand: (index: number) => void;
}

class ActionRecordComponent extends React.PureComponent<IActionRecordComponentProps> {
    public render(): JSX.Element {
        const { actionRecord, index, isExpanded } = this.props;

        return (
            <div className="action-record-container">
                <div className="action-record-timeline">
                    <Tooltip text={actionRecord.timestamp.toTimeString()}>
                        <div className="action-record-timeline-bubble" />
                    </Tooltip>
                </div>
                <div className="action-record">
                    <div className="action-record-header" onClick={this._toggleExpand}>
                        <Icon iconName="ChevronRightSmall" className={css("action-record-icon", isExpanded && "action-record-icon--expanded")} />
                        <div className="action-record-header-text">{actionRecord.actionName}</div>
                        <div className="action-record-actions">
                            <Button subtle={true} iconProps={{ iconName: "Delete" }} onClick={this._onClearAction} />
                        </div>
                    </div>
                    {this._renderActionContent()}
                </div>
            </div>
        );
    }

    private _renderActionContent(): JSX.Element | undefined {
        const { actionRecord, isExpanded } = this.props;

        if (isExpanded) {
            return (
                <div className="action-record-content">
                    <ActionMonitorObjectTree name="stack" object={actionRecord.stack} indentation={1} initiallyExpanded={false} valuesAsPre={true} />
                    <ActionMonitorObjectTree name="payload" object={actionRecord.payload} indentation={1} initiallyExpanded={true} />
                </div>
            );
        }
    }

    private _toggleExpand = () => {
        this.props.onToggleExpand(this.props.index);
    };

    private _onClearAction = (event: React.MouseEvent<any>) => {
        event.stopPropagation();
        this.props.onClearActionRecord(this.props.index);
    };
}
